import React, { useState, useRef } from "react";
import { crossIcon, Calculator, AttachFile } from '../../assets/images/index';
import { showErrorToast } from "../../components/Toaster/index";
import "../create-new-exercise/create-new-exercise.scss"; // reuse styles

interface FileUploadPanelProps {
  label: string;
  type: "content" | "graph-images";
  onFileChange: (images: string[] | string | null, type: "content" | "graph-images") => void;
  t: (key: string) => string;
}

const FileUploadPanel: React.FC<FileUploadPanelProps> = ({ label, type, onFileChange, t }) => {
  const [previews, setPreviews] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null); // ✅ declared inside component

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
      showErrorToast(t("onlyImagesAllowed"));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      if (type === "graph-images") {
        // Multiple images allowed
        setPreviews((prev) => {
          const updated = [...prev, result];
          onFileChange(updated, type);
          return updated;
        });
      } else {
        // Single image (overwrite)
        setPreviews([result]);
        onFileChange(result, type);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    if (type === "graph-images") {
      Array.from(files).forEach((file) => handleFile(file));
    } else {
      handleFile(files[0]);
    }

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData.items;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) handleFile(file);
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (!files) return;

    if (type === "graph-images") {
      Array.from(files).forEach((file) => handleFile(file));
    } else {
      handleFile(files[0]);
    }
  };

  const handleRemove = (index: number) => {
    if (type === "graph-images") {
      const updated = previews.filter((_, i) => i !== index);
      setPreviews(updated);
      onFileChange(updated, type);
    } else {
      setPreviews([]);
      onFileChange(null, type);
    }

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleClickDropzone = () => {
    inputRef.current?.click();
  };

  return (
    <div className="excercise_input_panel" onPaste={handlePaste} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
      <div className="dropzone" onClick={handleClickDropzone}>
        <input
          type="file"
          accept="image/*"
          multiple={type === "graph-images"}
          ref={inputRef}
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />
        <img src={AttachFile} alt="Upload" />
      </div>

      {previews.length > 0 && (
        <div className="preview-grid">
          {previews.map((src, idx) => (
            <div key={idx} className="preview-wrapper">
              <img src={src} alt={`preview-${idx}`} className="preview-img" />
              <button
                type="button"
                className="preview-remove-btn"
                onClick={() => handleRemove(idx)}
              >
                <img src={crossIcon} alt="Remove" />
              </button>
            </div>
          ))}
        </div>
      )}
      <textarea
        className="message-input"
        placeholder={t('exerciseInputWatermark')}
        disabled={true}
      />
    </div>
  );
};

export default FileUploadPanel;
